import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { createRoleGroup, fetchPermissions, fetchRoleGroups } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import ManagementHeader from '../management/ManagementHeader';
import MgField from '../management/MgField';
import { groupByCategory } from '../management/PermissionTree';
import '../management/management.css';

/* 권한 그룹 등록 — POST /users/role-groups { name, code, description, permissionIds }.
 * 권한은 GET /users/permissions 의 실제 권한 목록에서 고른다(권한 그룹 수정 팝업과 같은 목록). */
function RoleRegistrationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allPermissions, setAllPermissions] = useState([]);
  const [existingCodes, setExistingCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    permissionIds: []
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [permissionsData, roleGroupsData] = await Promise.all([fetchPermissions(), fetchRoleGroups()]);
        setAllPermissions(Array.isArray(permissionsData) ? permissionsData : []);
        // 코드 중복은 서버가 일반 오류(500)로만 알려 주므로 저장 전에 화면에서 먼저 확인한다
        setExistingCodes((Array.isArray(roleGroupsData) ? roleGroupsData : []).map((rg) => rg.code));
      } catch (err) {
        console.error('권한 목록 조회 실패:', err);
        alert(t('pages.user.roleRegister.alertLoadFail'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    const code = formData.code.trim();

    if (!name) {
      alert(t('pages.user.roleRegister.alertNameRequired'));
      return;
    }
    if (!code) {
      alert(t('pages.user.roleRegister.alertCodeRequired'));
      return;
    }
    if (existingCodes.includes(code)) {
      alert(t('pages.user.roleRegister.alertCodeDuplicate'));
      return;
    }

    try {
      setSubmitting(true);
      await createRoleGroup({
        name,
        code,
        description: formData.description.trim() || null,
        permissionIds: formData.permissionIds
      });
      alert(t('pages.user.roleRegister.alertCreateSuccess'));
      navigate('/role-management');
    } catch (error) {
      console.error('권한 등록 실패:', error);
      alert(t('pages.user.roleRegister.alertCreateFail'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm(t('pages.user.roleRegister.confirmCancel'))) {
      navigate('/role-management');
    }
  };

  return (
    <MainLayout>
      <div className="mg-page">
        <ManagementHeader
          rootLabel={t('sidebar.user.title')}
          icon="manage_accounts"
          backTo="/role-management"
          crumbs={[
            { label: t('sidebar.user.role'), to: '/role-management' },
            { label: t('pages.user.roleRegister.title') }
          ]}
        />

        <form className="mg-form" onSubmit={handleSubmit}>
          {/* 기본 정보 */}
          <div className="mg-panel">
            <h3 className="mg-panel-title">{t('pages.user.roleRegister.sectionBasic')}</h3>
            <div className="mg-grid">
              <MgField label={t('pages.user.roleRegister.nameLabel')} required>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={t('pages.user.roleRegister.namePlaceholder')} className="mg-input" required />
              </MgField>
              <MgField label={t('pages.user.roleRegister.codeLabel')} required>
                <input type="text" name="code" value={formData.code} onChange={handleInputChange} placeholder={t('pages.user.roleRegister.codePlaceholder')} className="mg-input" required />
              </MgField>
              <MgField label={t('pages.user.roleRegister.descriptionLabel')} full>
                <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder={t('pages.user.roleRegister.descriptionPlaceholder')} className="mg-textarea" rows="3" />
              </MgField>
            </div>
          </div>

          {/* 권한 설정 — 실제 권한 목록을 카테고리별로 고른다 */}
          <div className="mg-panel">
            <h3 className="mg-panel-title">
              {t('pages.user.roleRegister.sectionPermissions')}
              <span className="mg-panel-note">{t('pages.user.role.permissionSelected', { n: formData.permissionIds.length })}</span>
            </h3>
            {loading ? (
              <div className="mg-perm-empty">{t('pages.user.role.loading')}</div>
            ) : allPermissions.length === 0 ? (
              <div className="mg-perm-empty">{t('pages.user.role.misc.noPermissions')}</div>
            ) : (
              <div className="mg-perm-tree">
                {Object.entries(groupByCategory(allPermissions, t('pages.user.role.misc.etcCategory'))).map(([category, perms]) => (
                  <div key={category} className="mg-perm-group">
                    <div className="mg-perm-head">
                      <span className="material-symbols-outlined">folder</span>
                      {category}
                      <span className="mg-perm-count">({perms.length})</span>
                    </div>
                    <div className="mg-perm-items">
                      {perms.map((perm) => {
                        const checked = formData.permissionIds.includes(perm.id);
                        return (
                          <label key={perm.id} className={`mg-check ${checked ? 'checked' : ''}`}>
                            <input type="checkbox" checked={checked} onChange={() => handlePermissionToggle(perm.id)} />
                            <span className="mg-check-box"><span className="material-symbols-outlined">check</span></span>
                            {perm.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mg-form-actions">
            <button type="button" className="mg-btn secondary" onClick={handleCancel}>
              {t('pages.user.roleRegister.cancel')}
            </button>
            <button type="submit" className="mg-btn primary" disabled={submitting}>
              {t('pages.user.roleRegister.submit')}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}

export default RoleRegistrationPage;
